import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingStep from '../components/OnboardingStep';
import { toast } from 'sonner';
import { clearAllTimelineCaches } from '../utils/verifyUserData';

const OTPVerificationNew = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(['', '', '', '']);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const handleOtpChange = (index, value) => {
    // SMS auto-fill: distribute pasted digits across boxes (iOS QuickType / Android WebOTP)
    if (value.length > 1 && /^[0-9]+$/.test(value)) {
      const digits = value.slice(0, 4).split('');
      const newOtp = ['', '', '', ''];
      digits.forEach((d, i) => { newOtp[i] = d; });
      setOtp(newOtp);
      const lastIdx = Math.min(digits.length, 4) - 1;
      if (lastIdx >= 0 && lastIdx < 3) document.getElementById(`otp-${lastIdx + 1}`)?.focus();
      else document.getElementById(`otp-3`)?.blur();
      return;
    }
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 3) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleResend = () => {
    if (canResend) {
      setTimer(30);
      setCanResend(false);
      toast.success('OTP resent successfully!');
    }
  };

  const handleContinue = async () => {
    const otpValue = otp.join('');
    if (otpValue.length !== 4) {
      toast.error('Please enter complete OTP');
      return;
    }

    // For demo, accept any 4-digit OTP
    localStorage.setItem('user_authenticated', 'true');
    toast.success('✓ OTP verified!');
    
    // Skip API key step - proceed directly to story generation
    const birthData = JSON.parse(localStorage.getItem('birthData') || '{}');
    const birthCoords = JSON.parse(localStorage.getItem('birthCoordinates') || '{}');
    
    const storyData = {
      name: localStorage.getItem('userName') || 'User',
      dateOfBirth: birthData.date,
      timeOfBirth: birthData.time,
      placeOfBirth: birthCoords.place_name || localStorage.getItem('placeOfBirth') || 'Unknown',
      latitude: birthCoords.latitude || 28.6139,
      longitude: birthCoords.longitude || 77.2090
    };
    
    try {
      toast.info('🔮 Generating your past story...');
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      const response = await fetch(`${backendUrl}/api/story/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(storyData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to start story generation');
      }
      
      const data = await response.json();
      localStorage.setItem('prediction_id', data.prediction_id);
      // Clear stale audio cache flags so LoadingStory regenerates audio for new prediction
      localStorage.removeItem('audio_generation_complete');
      localStorage.removeItem('audio_prediction_id');
      localStorage.removeItem('past_reels_gen_state');
      localStorage.removeItem('watching_story');
      localStorage.removeItem('tts_currently_generating');
      localStorage.removeItem('audio_ready_domains');
      localStorage.removeItem('domain_progress');
      localStorage.removeItem('domain_insights_cache');
      localStorage.removeItem('ai_life_insights');
      clearAllTimelineCaches();
      
      // Clear server-side insights cache for this device
      try {
        const API = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
        const sessionId = localStorage.getItem('session_id') || data.prediction_id;
        fetch(`${API}/api/dashboard/enhanced-insights/${sessionId}`, { method: 'DELETE' }).catch(() => {});
      } catch (_) {}
      
      toast.success('✨ Story generation started!');
      navigate('/generating-story');
      
    } catch (err) {
      console.error('Story start error:', err);
      toast.error('Failed to start story: ' + err.message);
    }
  };
  
  return (
    <OnboardingStep
      step={5}
      totalSteps={5}
      title="Verify your number"
      reassurance="We'll send you updates about your chart"
      trustNote="We never spam. Unsubscribe anytime."
      onContinue={handleContinue}
      continueDisabled={otp.join('').length !== 4}
    >
      <div className="space-y-6">
        <div className="text-center text-sm text-stone-600">
          Code sent to +91 ••• ••• 4323
        </div>
        
        {/* OTP Input */}
        <div className="flex gap-3 justify-center">
          {[0, 1, 2, 3].map((i) => (
            <input
              key={i}
              id={`otp-${i}`}
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              name={i === 0 ? 'one-time-code' : `otp-${i}`}
              pattern="[0-9]*"
              maxLength={i === 0 ? 4 : 1}
              className="w-14 h-14 text-center text-2xl font-semibold rounded-input bg-stone-50 border-2 border-transparent focus:border-cosmic-500 focus:bg-white transition-all outline-none"
              value={otp[i]}
              onChange={(e) => handleOtpChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
            />
          ))}
        </div>
        
        {/* Timer / Resend */}
        <div className="text-center">
          {!canResend ? (
            <p className="text-sm text-stone-600">
              Resend code in <span className="text-cosmic-600 font-semibold">{timer}s</span>
            </p>
          ) : (
            <button
              onClick={handleResend}
              className="text-sm text-cosmic-600 hover:text-cosmic-700 font-medium transition-colors"
            >
              Resend Code
            </button>
          )}
        </div>
      </div>
    </OnboardingStep>
  );
};

export default OTPVerificationNew;